import { nextTick } from 'vue'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { toast } from 'vue3-toastify'
import type { ResultatsClassePayload } from '@/types/resultatsClasse'
import {
	buildPaginatedPages,
	waitForDeliberationDocumentReady,
} from '@/composables/useDeliberationPDF'

const PDF_CANVAS_SCALE = 2
const PDF_JPEG_QUALITY = 0.9

function addPdfPageNumberBottomRight(pdf: jsPDF, pageOneBased: number, totalPages: number) {
	if (totalPages < 1) return
	const w = pdf.internal.pageSize.getWidth()
	const h = pdf.internal.pageSize.getHeight()
	pdf.setFont('times', 'normal')
	pdf.setFontSize(10)
	pdf.setTextColor(45, 45, 45)
	pdf.text(`${pageOneBased}/${totalPages}`, w - 12, h - 8, { align: 'right' })
}

function setA4PageBox(pageEl: HTMLElement) {
	pageEl.style.width = '210mm'
	pageEl.style.height = '297mm'
	pageEl.style.overflow = 'hidden'
	pageEl.style.boxSizing = 'border-box'
}

function buildResultatsClasseFilename(payload: ResultatsClassePayload, anonymise: boolean): string {
	const session = payload.session.code || payload.session.nom || 'session'
	const spec = payload.specialite.code || payload.specialite.intitule || 'specialite'
	const safe = (s: string) => s.replace(/[^\w\-]+/g, '_').replace(/_+/g, '_')
	const suffix = anonymise ? '_anonyme' : ''
	return `Resultats_classe_${safe(session)}_${safe(spec)}${suffix}.pdf`
}

export async function generateResultatsClassePDF(
	payload: ResultatsClassePayload,
	options?: { anonymise?: boolean }
): Promise<void> {
	const anonymise = Boolean(options?.anonymise)
	const host = document.createElement('div')
	host.style.position = 'absolute'
	host.style.left = '-9999px'
	host.style.top = '0'
	host.style.width = '210mm'
	host.style.background = '#ffffff'
	host.style.boxSizing = 'border-box'
	document.body.appendChild(host)

	const componentContainer = document.createElement('div')
	host.appendChild(componentContainer)

	const { createApp } = await import('vue')
	const ResultatsClasseDocument = (
		await import('@/components/deliberation/templates/ResultatsClasseDocument.vue')
	).default

	const app = createApp(ResultatsClasseDocument, { payload, anonymise })
	const vm = app.mount(componentContainer) as any
	await nextTick()

	const preparingToastId = toast.loading('Préparation du document (chargement des données)...', {
		autoClose: false,
	})

	try {
		await waitForDeliberationDocumentReady(vm, { maxAttempts: 6000, intervalMs: 100 })
		await new Promise((resolve) => setTimeout(resolve, 250))
		toast.update(preparingToastId, {
			render: 'Génération du PDF...',
			type: 'default',
			isLoading: true,
			autoClose: false,
		})
	} catch {
		toast.update(preparingToastId, {
			render: 'Impossible de générer le PDF (données trop longues à charger).',
			type: 'error',
			isLoading: false,
			autoClose: 4000,
		})
		document.body.removeChild(host)
		app.unmount()
		throw new Error('Timeout chargement document résultats de classe')
	}

	const sheetEl = host.querySelector('.document-container') as HTMLElement | null
	if (!sheetEl) {
		toast.error("L'élément à imprimer est introuvable.")
		document.body.removeChild(host)
		app.unmount()
		throw new Error('Document container introuvable')
	}

	const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
	const pdfW = pdf.internal.pageSize.getWidth()
	const pdfH = pdf.internal.pageSize.getHeight()

	// Même pagination que « Résultats session » : découpe dynamique par lignes + espace signature.
	const paginatedPages = buildPaginatedPages(sheetEl)
	const pages =
		paginatedPages.length > 0
			? paginatedPages
			: (Array.from(sheetEl.querySelectorAll('.page')) as HTMLElement[])

	if (pages.length === 0) {
		toast.error('Aucune page à exporter.')
		document.body.removeChild(host)
		app.unmount()
		throw new Error('Aucune page')
	}

	for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
		const pageEl = pages[pageIndex]
		setA4PageBox(pageEl)
		await new Promise((resolve) => setTimeout(resolve, 50))

		const pageCanvas = await html2canvas(pageEl, {
			scale: PDF_CANVAS_SCALE,
			useCORS: true,
			allowTaint: true,
			backgroundColor: '#ffffff',
			height: pageEl.offsetHeight,
			width: pageEl.offsetWidth,
			windowWidth: pageEl.offsetWidth,
			windowHeight: pageEl.offsetHeight,
			logging: false,
		})

		const pxPerMm = pageCanvas.width / pdfW
		const pageHeightMm = pageCanvas.height / pxPerMm

		if (pageIndex > 0) pdf.addPage()

		pdf.addImage(
			pageCanvas.toDataURL('image/jpeg', PDF_JPEG_QUALITY),
			'JPEG',
			0,
			0,
			pdfW,
			Math.min(pageHeightMm, pdfH)
		)
		addPdfPageNumberBottomRight(pdf, pageIndex + 1, pages.length)
	}

	document.body.removeChild(host)
	app.unmount()

	const filename = buildResultatsClasseFilename(payload, anonymise)
	toast.update(preparingToastId, {
		render: 'PDF généré avec succès',
		type: 'success',
		isLoading: false,
		autoClose: 1500,
	})
	pdf.save(filename)
}
